import React from 'react';
import { Button, CircularProgress, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useThemeColor from '../hooks/useThemeColor';

const GreenSubmitButton = ({ 
  type = "button", 
  title, 
  size = "medium", 
  loading = false, 
  variant = "contained", 
  marginLeft = 1, 
  marginRight = 1, 
  marginTop = 1, 
  marginBottom = 1, 
  fontSize, 
  height,
  width,
  textTransform = "none",
  disabled = false, 
  borderRadius = 22, 
  onClick, 
  color: colorProp,
  startIcon,
  endIcon,
  fullWidth = false,
  elevation = 2,
  ...other 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px and below
  const accentHex = useThemeColor('--color-accent', '#2E7D32');
  const color = colorProp || accentHex; // theme accent unless a color is explicitly passed

  const themeConfig = createTheme({
    palette: {
      primary: {
        main: color,
        contrastText: "#ffffff"
      },
      secondary: {
        main: "#ffffff"
      },
    },
    shape: {
      borderRadius: borderRadius,
    },
    typography: {
      button: {
        textTransform: textTransform,
        fontWeight: 600,
        fontSize: fontSize || (size === 'small' ? '0.875rem' : size === 'large' ? '1rem' : '0.9375rem'),
      }
    }
  });

  const containedStyles = {
    backgroundColor: color,
    '&:hover': {
      backgroundColor: adjustColor(color, -20),
      boxShadow: `0 4px 12px ${adjustColor(color, 50, 0.3)}`,
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&.Mui-disabled': {
      backgroundColor: '#bdbdbd',
      color: '#757575',
    },
    transition: 'all 0.2s ease-in-out',
    boxShadow: elevation ? `0 ${elevation}px ${elevation * 2}px rgba(0,0,0,0.2)` : 'none',
  };

  const outlinedStyles = {
    borderColor: color,
    color: color,
    '&:hover': {
      borderColor: adjustColor(color, -20),
      backgroundColor: `${adjustColor(color, 0, 0.04)}`,
      borderWidth: '2px',
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&.Mui-disabled': {
      borderColor: '#bdbdbd',
      color: '#757575',
    },
    transition: 'all 0.2s ease-in-out',
  };

  const textStyles = {
    color: color,
    '&:hover': {
      backgroundColor: `${adjustColor(color, 0, 0.04)}`,
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&.Mui-disabled': {
      color: '#757575',
    },
    transition: 'all 0.2s ease-in-out',
  };

  function adjustColor(hex, percent, alpha = 1) {
    if (!hex.startsWith('#')) return hex;
    
    let R = parseInt(hex.substring(1, 3), 16);
    let G = parseInt(hex.substring(3, 5), 16);
    let B = parseInt(hex.substring(5, 7), 16);

    R = Math.min(255, Math.max(0, R + percent));
    G = Math.min(255, Math.max(0, G + percent));
    B = Math.min(255, Math.max(0, B + percent));

    if (alpha < 1) {
      return `rgba(${R}, ${G}, ${B}, ${alpha})`;
    }
    return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
  }

  const getVariantStyles = () => {
    switch(variant) {
      case 'contained':
        return containedStyles;
      case 'outlined':
        return outlinedStyles;
      case 'text':
        return textStyles;
      default:
        return containedStyles;
    }
  };

  const marginStyles = {
  marginLeft: typeof marginLeft === 'number' ? `${marginLeft * 8}px` : marginLeft,
  marginRight: typeof marginRight === 'number' ? `${marginRight * 8}px` : marginRight,
  marginTop: typeof marginTop === 'number' ? `${marginTop * 8}px` : marginTop,
  marginBottom: typeof marginBottom === 'number' ? `${marginBottom * 8}px` : marginBottom,
};

  return (
    <ThemeProvider theme={themeConfig}>
      <Button
        sx={{
          ...getVariantStyles(),
          ...marginStyles,
          height: height || (size === 'small' ? 36 : size === 'large' ? 48 : 42),
          width: isMobile ? (fullWidth ? '100%' : '60%') : (fullWidth ? '60%' : width),
          minWidth: isMobile ? '60%' : (width ? width : size === 'small' ? 64 : 88),
          padding: size === 'small' ? '4px 12px' : size === 'large' ? '10px 24px' : '6px 16px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          position: 'relative',
          ...other.sx
        }}
        aria-label={title || 'submit-button'}
        type={type}
        size={size}
        onClick={onClick}
        variant={variant}
        disabled={disabled || loading}
        startIcon={!loading && startIcon}
        endIcon={!loading && endIcon}
        disableElevation={!elevation}
        fullWidth={fullWidth || isMobile} // Also make fullWidth prop true on mobile
        {...other}
      >
        <span style={{ 
          opacity: loading ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {title}
        </span>
        
        {loading && (
          <CircularProgress
            color="secondary"
            size={size === 'small' ? 16 : size === 'large' ? 24 : 20}
            thickness={5}
            sx={{
              position: 'absolute',
              right: title ? '12px' : '50%',
              transform: title ? 'none' : 'translateX(50%)',
              color: variant === 'contained' ? '#ffffff' : color
            }}
          />
        )}
      </Button>
    </ThemeProvider>
  );
};

// Default props remain the same
GreenSubmitButton.defaultProps = {
  type: "button",
  size: "medium",
  loading: false,
  variant: "contained",
  marginLeft: 1,
  marginRight: 1,
  marginTop: 1,
  marginBottom: 1,
  textTransform: "none",
  disabled: false,
  borderRadius: 22,
  elevation: 2
};

export default GreenSubmitButton;